import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import { userRoleService } from "../../services/userRoleService";
import "../../assets/styles/AuthForm.css";
import "../../assets/styles/bootstrap.min.css";
import i1 from "../../assets/image/i1.png";
import logo from "../../assets/image/logo.png";
import { SignUpData } from "../../types/auth.types";
import { UserRoles } from "../../types/userRole";
import { VALIDATION, ERROR_MESSAGES, ROUTES } from "../../utils/constants";
// import Footer from '../../components/Layout/Footer';
// Constants

const SignUpPage: React.FC = () => {
    const [formData, setFormData] = useState({
        first_name: "",
        email: "",
        role_id: "",
        shift: "",
        password: "",
        confirm_password: "",
    });

    const [errors, setErrors] = useState({
        first_name: "",
        email: "",
        role_id: "",
        shift: "",
        password: "",
        confirm_password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userRoles, setUserRoles] = useState<UserRoles[]>([]);

    const navigate = useNavigate();


    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const roles = await userRoleService.getUserRole();
                setUserRoles(roles);
            } catch (error) {
                setError("Failed to load user roles. Please try again later.");
            }
        };

        fetchRoles();
    }, []);

    const validateForm = (): boolean => {
        const newErrors: any = {};

        if (!formData.first_name.trim()) newErrors.first_name = "Name is required";
        if (!VALIDATION.EMAIL_REGEX.test(formData.email)) newErrors.email = ERROR_MESSAGES.INVALID_EMAIL;
        if (!formData.role_id) newErrors.role_id = "User role is required";
        if (!formData.shift) newErrors.shift = "Shift is required";
        if (!VALIDATION.PASSWORD_REGEX.test(formData.password)) newErrors.password = ERROR_MESSAGES.PASSWORD_ERROR_MESSAGE;
        if (formData.password !== formData.confirm_password)
            newErrors.confirm_password = ERROR_MESSAGES.PASSWORDS_DONT_MATCH;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const updatedValue = name === "email" ? value.toLowerCase() : value;
        setFormData((prev) => ({ ...prev, [name]: updatedValue }));

        let errorMessage = "";

        // Field-specific validation
        if (name === "first_name") {
            if (!value.trim()) errorMessage = "Name is required";
        }

        if (name === "email") {
            if (!VALIDATION.EMAIL_REGEX.test(value)) errorMessage = ERROR_MESSAGES.INVALID_EMAIL;
        }

        if (name === "role_id") {
            if (!value) errorMessage = "User role is required";
        }

        if (name === "shift") {
            if (!value) errorMessage = "Shift is required";
        }

        if (name === "password") {
            if (!VALIDATION.PASSWORD_REGEX.test(value)) errorMessage = ERROR_MESSAGES.PASSWORD_ERROR_MESSAGE;
        }

        if (name === "confirmPassword") {
            if (value !== formData.password) errorMessage = ERROR_MESSAGES.PASSWORDS_DONT_MATCH;
        }

        // Update only the related error field
        setErrors((prev) => ({
            ...prev,
            [name]: errorMessage,
        }));

        // Clear general messages if user edits
        setError(null);
        setSuccessMessage(null);
    };


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (!validateForm()) return;

        const payload: SignUpData = {
            first_name: formData.first_name.trim(),
            email: formData.email.trim(),
            role_id: Number(formData.role_id),
            shift: formData.shift.trim(),
            password: formData.password,
            confirm_password: formData.confirm_password,
        };

        try {
            setIsSubmitting(true);
            await authService.signUp(payload);
            setSuccessMessage("Registration successful! Please wait for admin approval.");
            setFormData({
                first_name: "",
                email: "",
                role_id: "",
                shift: "",
                password: "",
                confirm_password: "",
            });
            setTimeout(() => navigate(ROUTES.SIGNIN), 5000);
        } catch (err: any) {
            setError(err.response?.data?.message || "Registration failed. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="auth-page d-flex align-items-center justify-content-center min-vh-100" style={{ overflowY: "auto", height: "100vh" }}>
                <div className="auth-form-wrapper p-4 shadow rounded bg-white" style={{ maxWidth: "800px", width: "100%" }}>
                    <div className="text-center mb-4">
                        <img src={logo} alt="Corecrate Logo" className="mb-3" style={{ height: "50px" }} />

                    </div>

                    {successMessage && (
                        <div
                            style={{
                                backgroundColor: "#d4edda",
                                color: "#155724",
                                border: "1px solid #c3e6cb",
                                padding: "10px",
                                borderRadius: "5px",
                                marginBottom: "15px",
                            }}
                        >
                            ✅ {successMessage}
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}

                    <form className="auth-form-register" onSubmit={handleSubmit}>
                        {/* Row 1: Name + Email */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label htmlFor="name">Full Name</label>
                                <input
                                    id="name"
                                    name="first_name"
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                />
                                {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                            </div>

                            <div className="col-md-6 form-group">
                                <label htmlFor="email">Email address</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>
                        </div>

                        {/* Row 2: User Role + Shift */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label htmlFor="role_id">User Role</label>
                                <select
                                    id="role_id"
                                    name="role_id"
                                    value={formData.role_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Role</option>
                                    {userRoles.map((role) =>
                                        role.id !== 6 && role.id !== 7 && role.name !== 'Internal User' && role.name !== 'user' && role.is_active == true  && (
                                            <option key={role.id} value={role.id} style={{ textTransform: 'capitalize' }}>
                                                {role.description || role.name}
                                            </option>
                                        )
                                    )}

                                </select>
                                {errors.role_id && <span className="error-message">{errors.role_id}</span>}
                            </div>

                            <div className="col-md-6 form-group">
                                <label htmlFor="shift">Shift</label>
                                <select
                                    id="shift"
                                    name="shift"
                                    value={formData.shift}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Shift</option>
                                    <option value="4">Flexible</option>
                                    <option value="1">Morning</option>
                                    <option value="2">Evening</option>
                                    <option value="3">Night</option>
                                </select>
                                {errors.shift && <span className="error-message">{errors.shift}</span>}
                            </div>
                        </div>

                        {/* Row 3: Password + Confirm Password */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label htmlFor="password">Create Password</label>
                                <div className="password-wrapper">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="********"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                                {errors.password && <span className="error-message">{errors.password}</span>}
                            </div>

                            <div className="col-md-6 form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <div className="password-wrapper">
                                    <input
                                        id="confirmPassword"
                                        name="confirm_password"
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="********"
                                        value={formData.confirm_password}
                                        onChange={handleChange}
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                                {errors.confirm_password && (
                                    <span className="error-message">{errors.confirm_password}</span>
                                )}
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <button type="submit" disabled={isSubmitting} className="submit-button w-50">
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner"></span> Registering...
                                    </>
                                ) : (
                                    "Register"
                                )}
                            </button>
                        </div>

                        <div className="form-footer text-center mt-3">
                            <p>
                                Already have an account? <Link to={ROUTES.SIGNIN}>Log In</Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
            {/* <Footer /> */}
        </>
    );
};

export default SignUpPage;
